import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Image, Video, FileText, Calendar, PlusCircle, X, Upload as UploadIcon, Link as LinkIcon, MapPin } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import PostCard from '../components/feed/PostCard';
import { useAuth } from '../contexts/AuthContext';
import { mockPosts } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Home = () => {
  const [postContent, setPostContent] = useState('');
  const [posts, setPosts] = useState(mockPosts);
  const { user } = useAuth();
  
  // Modal states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  
  // Form states
  const [photoData, setPhotoData] = useState({
    caption: '',
    images: []
  });
  
  const [videoData, setVideoData] = useState({
    caption: '',
    videoLink: '',
    thumbnail: null
  });
  
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    description: ''
  });
  
  const [articleData, setArticleData] = useState({
    title: '',
    content: '',
    coverImage: null
  });

  const handleCreatePost = () => {
    if (!postContent.trim()) {
      toast({
        title: "Post content required",
        description: "Please write something before posting.",
        variant: "destructive",
      });
      return;
    }

    const newPost = {
      id: Date.now().toString(),
      author: {
        name: user.name,
        title: user.title,
        avatar: user.avatar
      },
      content: postContent,
      timeAgo: 'now',
      likes: 0,
      comments: 0,
      shares: 0,
      isLiked: false
    };

    setPosts([newPost, ...posts]);
    setPostContent('');
    toast({
      title: "Post created!",
      description: "Your post has been shared with your network.",
    });
  };
  
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageUrls = files.map(file => {
      return URL.createObjectURL(file);
    });
    setPhotoData(prev => ({
      ...prev,
      images: [...prev.images, ...imageUrls]
    }));
  };
  
  const handleRemovePhoto = (index) => {
    setPhotoData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  const handlePostPhoto = () => {
    if (photoData.images.length === 0) {
      toast({
        title: "No images selected",
        description: "Please upload at least one image.",
        variant: "destructive",
      });
      return;
    }
    
    const newPost = {
      id: Date.now().toString(),
      author: {
        name: user.name,
        title: user.title,
        avatar: user.avatar
      },
      content: photoData.caption,
      images: photoData.images,
      timeAgo: 'now',
      likes: 0,
      comments: 0,
      shares: 0,
      isLiked: false
    };
    
    setPosts([newPost, ...posts]);
    setPhotoData({ caption: '', images: [] });
    setShowPhotoModal(false);
    toast({
      title: "Photo post created!",
      description: "Your photos have been shared.",
    });
  };
  
  const handlePostVideo = () => {
    if (!videoData.videoLink.trim()) {
      toast({
        title: "Video link required",
        description: "Please provide a video link.",
        variant: "destructive",
      });
      return;
    }
    
    const newPost = {
      id: Date.now().toString(),
      author: {
        name: user.name,
        title: user.title,
        avatar: user.avatar
      },
      content: videoData.caption,
      videoLink: videoData.videoLink,
      timeAgo: 'now',
      likes: 0,
      comments: 0,
      shares: 0,
      isLiked: false
    };
    
    setPosts([newPost, ...posts]);
    setVideoData({ caption: '', videoLink: '', thumbnail: null });
    setShowVideoModal(false);
    toast({
      title: "Video post created!",
      description: "Your video has been shared.",
    });
  };
  
  const handlePostEvent = () => {
    if (!eventData.title.trim() || !eventData.date) {
      toast({
        title: "Event details required",
        description: "Please provide event title and date.",
        variant: "destructive",
      });
      return;
    }
    
    const newPost = {
      id: Date.now().toString(),
      author: {
        name: user.name,
        title: user.title,
        avatar: user.avatar
      },
      content: `📅 Event: ${eventData.title}\n📍 ${eventData.location || 'Virtual'}\n🕐 ${eventData.date} at ${eventData.time}\n\n${eventData.description}`,
      eventData: eventData,
      timeAgo: 'now',
      likes: 0,
      comments: 0,
      shares: 0,
      isLiked: false
    };
    
    setPosts([newPost, ...posts]);
    setEventData({ title: '', date: '', time: '', location: '', description: '' });
    setShowEventModal(false);
    toast({
      title: "Event created!",
      description: "Your event has been shared.",
    });
  };
  
  const handlePostArticle = () => {
    if (!articleData.title.trim() || !articleData.content.trim()) {
      toast({
        title: "Article details required",
        description: "Please provide article title and content.",
        variant: "destructive",
      });
      return;
    }
    
    const newPost = {
      id: Date.now().toString(),
      author: {
        name: user.name,
        title: user.title,
        avatar: user.avatar
      },
      content: `📝 ${articleData.title}\n\n${articleData.content.substring(0, 200)}${articleData.content.length > 200 ? '...' : ''}`,
      articleData: articleData,
      timeAgo: 'now',
      likes: 0,
      comments: 0,
      shares: 0,
      isLiked: false
    };
    
    setPosts([newPost, ...posts]);
    setArticleData({ title: '', content: '', coverImage: null });
    setShowArticleModal(false);
    toast({
      title: "Article published!",
      description: "Your article has been shared.",
    });
  };

  const handleLikePost = (postId) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          isLiked: !post.isLiked,
          likes: post.isLiked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };

  return (
    <div className="space-y-6">
      {/* Create Post */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={user?.avatar} alt={user?.name} />
              <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <Textarea
                placeholder="Share your training insights, achievements, or ask for advice..."
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                className="border-none resize-none text-base p-0 focus-visible:ring-0"
                rows={3}
              />
            </div>
          </div>
          
          <Separator className="my-4" />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 hover:bg-blue-50"
                onClick={() => setShowPhotoModal(true)}
              >
                <Image className="w-4 h-4 mr-2" />
                Photo
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-green-600 hover:bg-green-50"
                onClick={() => setShowVideoModal(true)}
              >
                <Video className="w-4 h-4 mr-2" />
                Video
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-orange-600 hover:bg-orange-50"
                onClick={() => setShowEventModal(true)}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Event
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-purple-600 hover:bg-purple-50"
                onClick={() => setShowArticleModal(true)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Article
              </Button>
            </div>
            
            <Button 
              onClick={handleCreatePost}
              disabled={!postContent.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Post
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feed */}
      <div className="space-y-4">
        {posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            onLike={handleLikePost}
          />
        ))}
      </div>
      
      {/* Photo Modal */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center">
              <Image className="w-6 h-6 mr-2 text-blue-600" />
              Create Photo Post
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Photos
              </label>
              <label className="cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <UploadIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm font-medium text-gray-700 mb-1">Click to upload photos</p>
                  <p className="text-xs text-gray-500">PNG, JPG, JPEG (max 10MB each)</p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </label>
            </div>
            
            {photoData.images.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {photoData.images.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={img} alt="Upload" className="w-full h-32 object-cover rounded-lg" />
                    <button
                      onClick={() => handleRemovePhoto(idx)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caption
              </label>
              <Textarea
                value={photoData.caption}
                onChange={(e) => setPhotoData(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="Write a caption for your photos..."
                rows={3}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPhotoModal(false)}>
                Cancel
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handlePostPhoto}>
                Post Photos
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center">
              <Video className="w-6 h-6 mr-2 text-green-600" />
              Create Video Post
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video Link *
              </label>
              <Input
                type="url"
                value={videoData.videoLink}
                onChange={(e) => setVideoData(prev => ({ ...prev, videoLink: e.target.value }))}
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">YouTube, Vimeo, or any video platform link</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caption
              </label>
              <Textarea
                value={videoData.caption}
                onChange={(e) => setVideoData(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="Write about your video..."
                rows={3}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowVideoModal(false)}>
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handlePostVideo}>
                Post Video
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center">
              <Calendar className="w-6 h-6 mr-2 text-orange-600" />
              Create Event
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Title *
              </label>
              <Input
                value={eventData.title}
                onChange={(e) => setEventData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Leadership Training Workshop"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <Input
                  type="date"
                  value={eventData.date}
                  onChange={(e) => setEventData(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <Input
                  type="time"
                  value={eventData.time}
                  onChange={(e) => setEventData(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <Input
                value={eventData.location}
                onChange={(e) => setEventData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Virtual / New York Conference Center"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Description
              </label>
              <Textarea
                value={eventData.description}
                onChange={(e) => setEventData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your event, agenda, and what participants will learn..."
                rows={4}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEventModal(false)}>
                Cancel
              </Button>
              <Button className="bg-orange-600 hover:bg-orange-700" onClick={handlePostEvent}>
                Create Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Article Modal */}
      <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center">
              <FileText className="w-6 h-6 mr-2 text-purple-600" />
              Write Article
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Article Title *
              </label>
              <Input
                value={articleData.title}
                onChange={(e) => setArticleData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., 5 Strategies for Effective Virtual Training"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Image (Optional)
              </label>
              <label className="cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                  <UploadIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Click to upload cover image</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setArticleData(prev => ({ ...prev, coverImage: URL.createObjectURL(file) }));
                    }
                  }}
                  className="hidden"
                />
              </label>
              {articleData.coverImage && (
                <div className="mt-2 relative">
                  <img src={articleData.coverImage} alt="Cover" className="w-full h-48 object-cover rounded-lg" />
                  <button
                    onClick={() => setArticleData(prev => ({ ...prev, coverImage: null }))}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Article Content *
              </label>
              <Textarea
                value={articleData.content}
                onChange={(e) => setArticleData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Share your expertise, insights, and knowledge with the training community..."
                rows={10}
                required
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowArticleModal(false)}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handlePostArticle}>
                Publish Article
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;